import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import MovieGuessGame from '../components/MovieGuessGame';
import { Send, Sparkles, Zap, Film, Award, TrendingUp, RotateCcw, Volume2, VolumeX, Newspaper, Globe, Mic, Square, Image as ImageIcon, Gamepad2, Download, Copy, Shield, FileText, X, Crown } from 'lucide-react';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { downloadMedia } from '../utils/downloadMedia';
import MediaLightbox from '../components/MediaLightbox';
import MoviePosterScanner from '../components/MoviePosterScanner';
import VoiceClonePanel from '../components/myia/VoiceClonePanel';
import RealTimeTranslator from '../components/myia/RealTimeTranslator';
import AudioExtractor from '../components/myia/AudioExtractor';
import PitchStudio, { pitchTexts } from '../components/myia/PitchStudio';
import DubbingPanel from '../components/myia/DubbingPanel';
import MediaGeneratorModal from '../components/myia/MediaGeneratorModal';
import GreenIAPanel from '../components/myia/GreenIAPanel';
import SubscriptionPanel, { SubscriptionButton, SubscriptionModal } from '../components/myia/SubscriptionPanel';
import { usePremiumEnabled } from '../hooks/usePremiumEnabled';
import { myiaTexts } from '../utils/myiaTranslations';
import DeesseVoiceModal from '../components/myia/DeesseVoiceModal';
import { deesseTexts } from '../utils/deesseTranslations';
import DeesseAvatar from '../components/myia/DeesseAvatar';
import { useGreenIAStatus } from '../hooks/useGreenIAStatus';
import { africaTexts } from '../components/GptAfricaCard';
import { ReportAIContentButton } from '../components/ReportAIContent';
import { BURGER_I18N } from '../components/TikTokFeed';
import { useNavigate } from 'react-router-dom';

const FilmIA = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [imageMode, setImageMode] = useState(false); // true = next vocal => generate image
  const [generatingImage, setGeneratingImage] = useState(false);
  const [imageQuota, setImageQuota] = useState(null); // {limit,used,remaining}
  const [showMovieGame, setShowMovieGame] = useState(false);
  const [showPosterScanner, setShowPosterScanner] = useState(false);
  const [showMediaGen, setShowMediaGen] = useState(false);
  const [showGreenIA, setShowGreenIA] = useState(false);
  const greenIA = useGreenIAStatus();
  const [showSubscription, setShowSubscription] = useState(false);
  const [showPitchStudio, setShowPitchStudio] = useState(false);
  const [showDeesse, setShowDeesse] = useState(false);
  const premiumOn = usePremiumEnabled();
  // Regional language picker — when set, the IA must answer in this
  // Guianese regional language using the community-built corpus.
  const [regionalLang, setRegionalLang] = useState(() => {
    try { return localStorage.getItem('dsia_regional_lang_ia') || ''; } catch { return ''; }
  });
  const [regionalLangOptions, setRegionalLangOptions] = useState([]);
  const [africaLangOptions, setAfricaLangOptions] = useState([]);
  const [regionalEligible, setRegionalEligible] = useState(false);
  useEffect(() => {
    axios.get('/api/regional-lang/info')
      .then(r => {
        setRegionalLangOptions(r.data?.languages || []);
        setRegionalEligible(!!r.data?.is_eligible);
      })
      .catch(() => {});
  }, []);
  const setRegionalLangPersist = (id) => {
    setRegionalLang(id);
    try { localStorage.setItem('dsia_regional_lang_ia', id); } catch {}
  };
  const [lightbox, setLightbox] = useState(null); // {type, src} | null
  const audioRef = useRef(null);
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  useEffect(() => {
    axios.get('/api/gpt-africa/info', { params: { lang: language } }).then((r) => setAfricaLangOptions(r.data?.languages || [])).catch(() => {});
  }, [language]);
  const M = myiaTexts(language);
  const keyboardHeight = useKeyboardHeight();

  const suggestedPrompts = [
    { icon: <Newspaper className="w-5 h-5" />, text: (t('filmIA.suggestions') || [])[0] || "Raconte-moi les actualités du jour" },
    { icon: <Sparkles className="w-5 h-5" />, text: (t('filmIA.suggestions') || [])[5] || "Explique-moi le cinéma français" }
  ];

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await axios.get('/api/film-ia/history');
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const handleSend = async (messageText = input) => {
    if (!messageText.trim() || loading) return;

    const userMessage = messageText;
    setInput('');
    setLoading(true);

    // Add user message immediately
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    try {
      const response = await axios.post('/api/film-ia/chat', { message: userMessage, regional_lang: regionalLang || undefined });
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        flagged: response.data.flagged || false,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de connexion');
      // Remove user message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestedPrompt = (promptText) => {
    setInput(promptText);
    handleSend(promptText);
  };

  const handleNewConversation = async () => {
    if (messages.length === 0) return;
    
    const confirm = window.confirm('Voulez-vous vraiment commencer une nouvelle conversation ? Celle-ci sera réinitialisée.');
    
    if (confirm) {
      try {
        await axios.post('/api/film-ia/clear-history');
        setMessages([]);
        setInput('');
        stopAudio();
        toast.success('Conversation réinitialisée !');
      } catch (error) {
        console.error('Error clearing history:', error);
        setMessages([]);
        setInput('');
        toast.success(t("filmIA.newConversation"));
      }
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const formats = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'];
      let mimeType = '';
      for (const fmt of formats) {
        if (MediaRecorder.isTypeSupported(fmt)) { mimeType = fmt; break; }
      }
      const options = mimeType ? { mimeType } : {};
      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const actualMime = mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: actualMime });
        const ext = actualMime.includes('mp4') ? 'mp4' : 'webm';
        await handleVoiceMessage(blob, ext);
      };
      
      recorder.start(100);
      setIsRecording(true);
      setRecordingTime(0);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 60) { stopRecording(); return 60; }
          return prev + 1;
        });
      }, 1000);
    } catch {
      toast.error("Impossible d'accéder au microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
    }
  };

  const handleVoiceMessage = async (audioBlob, ext) => {
    // If image mode is active → call voice-to-image instead
    if (imageMode) {
      return handleVoiceToImage(audioBlob, ext);
    }
    setLoading(true);
    
    // Add user message placeholder
    setMessages(prev => [...prev, {
      role: 'user',
      content: 'Message vocal...',
      isVoice: true,
      timestamp: new Date().toISOString()
    }]);
    
    try {
      // Step 1: Transcribe audio
      const formData = new FormData();
      formData.append('audio', new File([audioBlob], `voice.${ext}`, { type: audioBlob.type }));
      const sttRes = await axios.post('/api/film-ia/speech-to-text', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const transcribedText = sttRes.data.text;
      
      // Update user message with transcribed text
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: 'user',
          content: transcribedText,
          isVoice: true,
          timestamp: new Date().toISOString()
        };
        return updated;
      });
      
      // Step 2: Send to chat
      const chatRes = await axios.post('/api/film-ia/chat', { message: transcribedText, regional_lang: regionalLang || undefined });
      const responseText = chatRes.data.response;
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: responseText,
        isVoice: true,
        timestamp: new Date().toISOString()
      }]);
      
      // Step 3: Auto-play TTS response (voice mode)
      try {
        const ttsRes = await axios.post('/api/film-ia/text-to-speech', {
          text: responseText.substring(0, 3000),
          voice: 'nova'
        });
        if (ttsRes.data.audio) {
          const audio = new Audio(`data:audio/mp3;base64,${ttsRes.data.audio}`);
          if (audioRef.current) {
            audioRef.current.pause();
          }
          audioRef.current = audio;
          audio.play();
          setPlayingAudio('auto');
          audio.onended = () => setPlayingAudio(null);
        }
      } catch {
        // TTS failed silently, text response is still shown
      }
      
    } catch (error) {
      console.error('Voice message error:', error);
      toast.error('Erreur de transcription');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleVoiceToImage = async (audioBlob, ext) => {
    setGeneratingImage(true);
    setMessages(prev => [...prev, {
      role: 'user',
      content: '🎨 Génération d\'image depuis ton vocal…',
      isVoice: true,
      timestamp: new Date().toISOString(),
    }]);
    try {
      const formData = new FormData();
      formData.append('audio', new File([audioBlob], `voice.${ext}`, { type: audioBlob.type }));
      const res = await axios.post('/api/voice-to-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const { prompt, image_base64, remaining } = res.data;
      setImageQuota(prev => ({ ...(prev || { limit: 5, used: 0 }), remaining, used: 5 - remaining }));
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: 'user',
          content: prompt,
          isVoice: true,
          timestamp: new Date().toISOString(),
        };
        updated.push({
          role: 'assistant',
          content: `Image générée à partir de votre vocal :`,
          image_base64,
          remaining,
          timestamp: new Date().toISOString(),
        });
        return updated;
      });
      toast.success(`Image créée ! ${remaining} restantes aujourd'hui.`);
      setImageMode(false);
    } catch (e) {
      console.error('voice-to-image error', e);
      const detail = e?.response?.data?.detail || 'Erreur de génération';
      toast.error(detail);
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setGeneratingImage(false);
    }
  };

  const fetchImageQuota = async () => {
    try {
      const res = await axios.get('/api/voice-to-image/quota');
      setImageQuota(res.data);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    fetchImageQuota();
  }, []);


  const playAudio = async (text, messageIndex) => {
    // Stop any currently playing audio
    stopAudio();
    
    setLoadingAudio(messageIndex);
    
    try {
      const response = await axios.post('/api/film-ia/text-to-speech', {
        text: text,
        voice: 'nova'  // Energetic, upbeat voice
      });
      
      if (response.data.audio) {
        const audioSrc = `data:audio/mp3;base64,${response.data.audio}`;
        
        if (audioRef.current) {
          audioRef.current.src = audioSrc;
          audioRef.current.play();
          setPlayingAudio(messageIndex);
          
          audioRef.current.onended = () => {
            setPlayingAudio(null);
          };
        }
      }
    } catch (error) {
      console.error('Error generating audio:', error);
      toast.error('Erreur lors de la génération audio');
    } finally {
      setLoadingAudio(null);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingAudio(null);
  };

  return (
    <div className={`min-h-screen text-white flex flex-col pb-16 ${isDark ? 'bg-black' : ''}`} style={!isDark ? { background: 'var(--theme-bg)' } : {}}>
      {/* Header */}
      <div className={`p-6 border-b border-white/10 ${isDark ? 'bg-gradient-to-r from-magenta-500/10 to-violet-500/10' : 'bg-white/10'}`}>
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center space-x-3">
            <button onClick={() => setShowDeesse(true)} className="w-12 h-12 rounded-2xl overflow-hidden border border-fuchsia-400/50 shadow-lg shadow-fuchsia-900/40 hover:scale-105 transition-transform" title={deesseTexts(language).eiTalkAI} data-testid="header-deesse-avatar">
              <img src="/app-icon.png" alt="Déesse" className="w-full h-full object-cover" />
            </button>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-magenta-400 to-violet-400 bg-clip-text text-transparent">
                {t('filmIA.title')}
              </h1>
              <p className="text-sm text-gray-400">{t('filmIA.subtitle')}</p>
            </div>
          </div>
          
          {/* New Conversation Button + Déesse EI quick access (always visible once a chat started) */}
          {messages.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleNewConversation}
                className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/10 rounded-full transition-all group"
                title={t("filmIA.newConversation")}
              >
                <RotateCcw className="w-4 h-4 group-hover:rotate-180 transition-transform duration-300" />
                <span className="text-sm font-medium hidden md:inline">{t("filmIA.newConversation")}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 pb-24">
        <div className="max-w-4xl mx-auto space-y-6">
          {messages.length === 0 ? (
            <div className="h-full flex items-center justify-center py-20">
              <div className="text-center max-w-2xl">
                <div className="flex flex-col items-center mb-6" data-testid="deesse-welcome-avatar">
                  <DeesseAvatar state="idle" size="lg" />
                  <button
                    onClick={() => setShowDeesse(true)}
                    className="mt-5 px-6 py-3 rounded-full bg-gradient-to-r from-fuchsia-500 to-pink-600 hover:from-fuchsia-400 hover:to-pink-500 font-semibold text-sm flex items-center gap-2 shadow-lg shadow-fuchsia-900/40 transition-transform active:scale-95"
                    data-testid="talk-to-deesse"
                  >
                    <Mic className="w-4 h-4" /> {deesseTexts(language).eiTalkAI}
                  </button>
                </div>
                <h3 className="text-3xl font-bold mb-4">{t('filmIA.greeting')} 👋</h3>
                <p className="text-xl text-gray-400 mb-8">
                  {t('filmIA.greetingText')}
                </p>

                {/* Suggested Prompts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-8">
                  {suggestedPrompts.map((prompt, i) => (
                    <button
                      key={i}
                      onClick={() => handleSuggestedPrompt(prompt.text)}
                      className="p-4 bg-white/5 border border-white/10 rounded-2xl hover:border-magenta-500/50 hover:bg-white/10 transition-all text-left group"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-gradient-to-br from-magenta-500 to-violet-600 rounded-lg group-hover:scale-110 transition-transform">
                          {prompt.icon}
                        </div>
                        <span className="text-sm font-medium">{prompt.text}</span>
                      </div>
                    </button>
                  ))}
                </div>
                {/* Regional language picker — only visible to users whose
                    UI language is FR / EN / NL (the corpus contributors). */}
                {regionalEligible && (
                <div className="mt-4 p-4 rounded-2xl bg-gradient-to-r from-emerald-500/10 to-amber-500/10 border border-emerald-500/30" data-testid="filmia-regional-lang-card">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="text-2xl shrink-0">🇬🇫</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-extrabold tracking-tight bg-gradient-to-r from-emerald-300 to-amber-300 bg-clip-text text-transparent mb-0.5" data-testid="filmia-gpt-guyane-title">
                        GPT Guyane
                      </p>
                      <h4 className="font-bold text-emerald-200 text-sm">
                        {t('filmIA.regionalLangTitle', 'Réponse en langue régionale de Guyane')}
                      </h4>
                      <p className="text-[11px] text-emerald-200/70 mt-0.5 leading-snug">
                        {t('filmIA.regionalLangSubtitle', "L'IA répondra dans la langue choisie en s'appuyant sur le corpus contribué par la communauté.")}
                      </p>
                    </div>
                  </div>
                  <select
                    value={regionalLang}
                    onChange={(e) => setRegionalLangPersist(e.target.value)}
                    className="w-full bg-black/40 border border-emerald-500/40 rounded-full px-4 py-2 text-xs font-semibold focus:outline-none focus:border-emerald-400"
                    data-testid="filmia-regional-lang-select"
                  >
                    <option value="" className="bg-gray-900">
                      {t('filmIA.regionalLangOff', 'Aucune (langue principale)')}
                    </option>
                    {regionalLangOptions.map((l) => (
                      <option key={l.id} value={l.id} className="bg-gray-900">{l.label}</option>
                    ))}
                  </select>
                </div>
                )}

                {/* v50.10.8c — GPT Africa (all 13 UI languages), just below GPT Guyane */}
                <div className="mt-4 p-4 rounded-2xl bg-gradient-to-r from-amber-500/10 via-emerald-500/10 to-rose-500/10 border border-amber-500/30" data-testid="filmia-gpt-africa-card">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="text-2xl shrink-0">🌍</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-extrabold tracking-tight bg-gradient-to-r from-amber-300 to-emerald-300 bg-clip-text text-transparent mb-0.5" data-testid="filmia-gpt-africa-title">GPT Africa</p>
                      <h4 className="font-bold text-amber-200 text-sm">{africaTexts(language).title}</h4>
                      <p className="text-[11px] text-amber-200/70 mt-0.5 leading-snug">{t('filmIA.regionalLangSubtitle', "L'IA répondra dans la langue choisie en s'appuyant sur le corpus contribué par la communauté.")}</p>
                    </div>
                  </div>
                  <select
                    value={africaLangOptions.some((l) => l.id === regionalLang) ? regionalLang : ''}
                    onChange={(e) => setRegionalLangPersist(e.target.value)}
                    className="w-full bg-black/40 border border-amber-500/40 rounded-full px-4 py-2 text-xs font-semibold focus:outline-none focus:border-amber-400"
                    data-testid="filmia-gpt-africa-select"
                  >
                    <option value="" className="bg-gray-900">{t('filmIA.regionalLangOff', 'Aucune (langue principale)')}</option>
                    {africaLangOptions.map((l) => (
                      <option key={l.id} value={l.id} className="bg-gray-900">{l.label}</option>
                    ))}
                  </select>
                </div>

                {/* Mini-game launcher */}
                <button
                  onClick={() => setShowMovieGame(true)}
                  className="mt-8 w-full p-5 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/40 rounded-2xl hover:from-yellow-500/30 hover:to-orange-500/30 transition-all text-left group flex items-center gap-4"
                  data-testid="open-movie-game"
                >
                  <div className="p-3 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl group-hover:scale-110 transition-transform">
                    <Gamepad2 className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-yellow-300">{t('filmIA.movieGameTitle')}</h4>
                    <p className="text-xs text-gray-400 mt-1">{t('filmIA.movieGameSubtitle')}</p>
                  </div>
                </button>

                {/* Movie poster scanner launcher */}
                <button
                  onClick={() => setShowPosterScanner(true)}
                  className="mt-3 w-full p-5 bg-gradient-to-r from-fuchsia-500/20 to-purple-500/20 border border-fuchsia-500/40 rounded-2xl hover:from-fuchsia-500/30 hover:to-purple-500/30 transition-all text-left group flex items-center gap-4"
                  data-testid="open-poster-scanner"
                >
                  <div className="p-3 bg-gradient-to-br from-fuchsia-500 to-purple-600 rounded-xl group-hover:scale-110 transition-transform">
                    <Film className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-fuchsia-300">{t('posterScanner.cameraCTA', 'Scanner une affiche de film')}</h4>
                    <p className="text-xs text-gray-400 mt-1">{t('posterScanner.cameraDescription', 'Photographiez une affiche au cinéma et laissez Déesse identifier le film.')}</p>
                  </div>
                </button>

                {/* v50.10.9 — Avatar IA (aussi dans le burger) juste sous le scanner d'affiche */}
                <div className="mt-3">
                  <button
                    onClick={() => navigate('/studio')}
                    className="w-full p-5 bg-gradient-to-r from-fuchsia-500/20 to-violet-500/20 border border-fuchsia-500/40 rounded-2xl hover:from-fuchsia-500/30 hover:to-violet-500/30 transition-all text-left group flex items-center gap-4"
                    data-testid="open-avatar-ia"
                  >
                    <div className="p-3 bg-gradient-to-br from-fuchsia-500 to-violet-600 rounded-xl group-hover:scale-110 transition-transform">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-fuchsia-200">{(BURGER_I18N[language] || BURGER_I18N.en).avaTitle}</h4>
                      <p className="text-xs text-gray-400 mt-1">{(BURGER_I18N[language] || BURGER_I18N.en).avaSub}</p>
                    </div>
                  </button>
                </div>
                <div className="mt-3">
                  <button
                    onClick={() => setShowMediaGen(true)}
                    className="w-full p-5 bg-gradient-to-r from-pink-500/20 to-magenta-500/20 border border-pink-500/40 rounded-2xl hover:from-pink-500/30 hover:to-magenta-500/30 transition-all text-left group flex items-center gap-4"
                    data-testid="open-media-generator-section"
                  >
                    <div className="p-3 bg-gradient-to-br from-pink-500 to-magenta-600 rounded-xl group-hover:scale-110 transition-transform">
                      <ImageIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-bold text-pink-200">{t('myiaVoice.mgSectionTitle', 'Générer une image ou une vidéo')}</h4>
                        {premiumOn && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-yellow-500/15 border border-yellow-500/40 text-[9px] font-bold text-yellow-300 uppercase tracking-wide" data-testid="media-gen-premium-badge">
                            <Crown className="w-2.5 h-2.5" /> Premium
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{t('myiaVoice.mgSectionSub', 'Décrivez à la voix ou par écrit, importez jusqu\u2019à 2 images de référence.')}</p>
                    </div>
                  </button>
                </div>

                {/* Studio Pitch — modal launcher, right below Green IA (v50.10.1) */}
                <button
                  onClick={() => setShowPitchStudio(true)}
                  className="mt-3 w-full p-5 bg-gradient-to-r from-pink-500/20 to-fuchsia-500/20 border border-pink-500/40 rounded-2xl hover:from-pink-500/30 hover:to-fuchsia-500/30 transition-all text-left group flex items-center gap-4"
                  data-testid="open-pitch-studio"
                >
                  <div className="p-3 bg-gradient-to-br from-pink-500 to-fuchsia-600 rounded-xl group-hover:scale-110 transition-transform">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-bold text-pink-200">{pitchTexts(language).title}</h4>
                      {premiumOn && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-yellow-500/15 border border-yellow-500/40 text-[9px] font-bold text-yellow-300 uppercase tracking-wide" data-testid="pitch-studio-premium-badge">
                          <Crown className="w-2.5 h-2.5" /> Premium
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{pitchTexts(language).sub}</p>
                  </div>
                </button>

                {/* Premium subscription — button that opens a modal (v50.14) */}
                <SubscriptionButton
                  onOpen={() => setShowSubscription(true)}
                />

                {/* New voice-centric sections (v50.7) */}
                <div className="mt-3"><VoiceClonePanel /></div>
                <div className="mt-3"><RealTimeTranslator /></div>
                <div className="mt-3"><AudioExtractor /></div>
                {/* v50.16 — Professional video dubbing (just above the regional-language card) */}
                <div className="mt-3"><DubbingPanel /></div>

                {/* Green IA — anti-deepfake modal launcher (v50.9) — masqué si cédé (v50.10.8) */}
                {greenIA.enabled && <button
                  onClick={() => setShowGreenIA(true)}
                  className="mt-3 w-full p-5 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/40 rounded-2xl hover:from-green-500/30 hover:to-emerald-500/30 transition-all text-left group flex items-center gap-4"
                  data-testid="open-green-ia"
                >
                  <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl group-hover:scale-110 transition-transform">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-green-200">{M.giCta}</h4>
                    <p className="text-xs text-gray-400 mt-1">{M.giCtaSub}</p>
                  </div>
                </button>}

              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <img src="/app-icon.png" alt="Déesse" className="w-10 h-10 rounded-full object-cover mr-3 flex-shrink-0 border border-fuchsia-400/40" />
                  )}
                  <div className={`max-w-3xl ${msg.role === 'assistant' ? '' : ''}`}>
                    <div
                      className={`px-6 py-4 rounded-3xl ${
                        msg.role === 'user'
                          ? 'bg-gradient-to-r from-magenta-500 to-violet-600 text-white'
                          : msg.flagged
                          ? 'bg-red-500/10 text-white border border-red-500/20'
                          : 'bg-white/10 text-white border border-white/10'
                      }`}
                    >
                      <p className="whitespace-pre-wrap leading-relaxed">
                        {(() => {
                          // Render any URL (http/https) as an external clickable link.
                          const parts = msg.content.split(/(https?:\/\/[^\s)]+)/g);
                          return parts.map((p, i) => (
                            /^https?:\/\//.test(p) ? (
                              <a
                                key={i}
                                href={p}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-cyan-400 underline hover:text-cyan-300 break-all"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {p}
                              </a>
                            ) : (
                              <React.Fragment key={i}>{p}</React.Fragment>
                            )
                          ));
                        })()}
                      </p>
                      {msg.image_base64 && (
                        <div className="mt-3 relative inline-block group">
                          <img
                            src={`data:image/png;base64,${msg.image_base64}`}
                            alt="Image générée"
                            className="rounded-2xl w-full max-w-sm border border-white/10 cursor-zoom-in"
                            data-testid={`generated-image-${index}`}
                            onClick={() => setLightbox({ type: 'image', src: `data:image/png;base64,${msg.image_base64}` })}
                          />
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await downloadMedia({
                                  base64: msg.image_base64,
                                  mime: 'image/png',
                                  filename: `destine-ia-${msg.timestamp || Date.now()}`,
                                });
                                toast.success('Image téléchargée');
                              } catch (e) {
                                console.error(e);
                                toast.error('Téléchargement impossible');
                              }
                            }}
                            className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full backdrop-blur-sm transition-all opacity-90 hover:opacity-100"
                            data-testid={`download-generated-image-${index}`}
                            aria-label="Télécharger l'image"
                            title="Télécharger l'image"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <span className={`text-xs ${
                          msg.role === 'user' ? 'text-magenta-100' : 'text-gray-500'
                        }`}>
                          {new Date(msg.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {msg.role === 'assistant' && (
                          <>
                            <ReportAIContentButton
                              feature={msg.video_url ? 'ai_video' : msg.image_base64 ? 'ai_image' : 'deesse_chat'}
                              contentText={msg.content}
                              mediaUrl={msg.video_url || ''}
                              iconOnly
                              className="ml-3 p-1.5 rounded-full hover:bg-orange-500/20 text-gray-400 hover:text-orange-300 transition-colors"
                              testId={`ai-report-btn-${index}`}
                            />
                            <button
                              data-testid={`copy-btn-${index}`}
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(msg.content);
                                  toast.success('Réponse copiée');
                                } catch (_) {
                                  // Fallback for older browsers / iOS WebView
                                  const ta = document.createElement('textarea');
                                  ta.value = msg.content;
                                  ta.style.position = 'fixed'; ta.style.opacity = '0';
                                  document.body.appendChild(ta);
                                  ta.select();
                                  try { document.execCommand('copy'); toast.success('Réponse copiée'); }
                                  catch { toast.error('Copie impossible'); }
                                  ta.remove();
                                }
                              }}
                              className="ml-3 p-1.5 rounded-full hover:bg-white/10 transition-colors"
                              title="Copier la réponse"
                            >
                              <Copy className="w-4 h-4 text-gray-400 hover:text-white" />
                            </button>
                            <button
                              data-testid={`tts-btn-${index}`}
                              onClick={() => playingAudio === index ? stopAudio() : playAudio(msg.content, index)}
                              disabled={loadingAudio === index}
                              className="ml-1 p-1.5 rounded-full hover:bg-white/10 transition-colors disabled:opacity-50"
                              title={playingAudio === index ? 'Arrêter la lecture' : 'Écouter'}
                            >
                              {loadingAudio === index ? (
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              ) : playingAudio === index ? (
                                <VolumeX className="w-4 h-4 text-magenta-400" />
                              ) : (
                                <Volume2 className="w-4 h-4 text-gray-400 hover:text-white" />
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="w-10 h-10 bg-gradient-to-br from-magenta-500 to-violet-600 rounded-full flex items-center justify-center mr-3">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div className="max-w-3xl px-6 py-4 rounded-3xl bg-white/10 border border-white/10">
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-magenta-500 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-magenta-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                      <div className="w-2 h-2 bg-magenta-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
          {/* Spacer for input + keyboard */}
          <div style={{ height: keyboardHeight > 0 ? keyboardHeight + 100 : 100 }} />
        </div>
      </div>

      {/* Input - Adapts to keyboard height on iOS */}
      <form
        onSubmit={(e) => { e.preventDefault(); handleSend(); }}
        className="fixed left-0 right-0 p-4 border-t border-white/10 bg-black/95 backdrop-blur-md z-50 transition-all duration-200"
        style={{ bottom: keyboardHeight > 0 ? keyboardHeight : 64 }}
      >
        <div className="max-w-4xl mx-auto">
          {/* Recording indicator */}
          {isRecording && (
            <div className="flex items-center justify-between mb-3 px-4 py-2 bg-red-500/15 border border-red-500/30 rounded-full">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                <span className="text-red-400 text-sm">Enregistrement... {recordingTime}s</span>
              </div>
              <button
                type="button"
                onClick={stopRecording}
                className="p-1.5 bg-red-500 rounded-full"
              >
                <Square className="w-3.5 h-3.5" fill="white" />
              </button>
            </div>
          )}
          <div className="flex items-center space-x-1.5 sm:space-x-2">
            {/* Image/video generator button — opens rich modal (voice OR text + up to 2 refs) */}
            <button
              type="button"
              onClick={() => setShowMediaGen(true)}
              disabled={loading || generatingImage}
              className="p-2.5 sm:p-3 rounded-full transition-all flex items-center justify-center flex-shrink-0 relative bg-gradient-to-br from-pink-500 to-magenta-600 shadow-lg shadow-magenta-500/40 hover:shadow-magenta-500/60"
              data-testid="film-ia-image-mode-btn"
              title="Générer image ou vidéo (voix ou texte)"
            >
              <ImageIcon className="w-5 h-5" />
              {imageQuota && (
                <span className="absolute -top-1 -right-1 px-1.5 py-0.5 bg-yellow-500 text-black text-[9px] font-bold rounded-full leading-none">
                  {imageQuota.remaining}
                </span>
              )}
            </button>
            {/* Voice recording button */}
            <button
              type="button"
              onClick={isRecording ? stopRecording : startRecording}
              disabled={loading || generatingImage}
              className={`p-2.5 sm:p-3 rounded-full transition-all flex items-center justify-center flex-shrink-0 ${
                isRecording
                  ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                  : 'bg-white/10 hover:bg-white/20'
              }`}
              data-testid="film-ia-voice-btn"
              title={imageMode ? 'Décris l\'image en vocal' : 'Envoyer un message vocal'}
            >
              <Mic className="w-5 h-5" />
            </button>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onFocus={() => {
                setTimeout(() => {
                  messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                }, 300);
              }}
              placeholder={t('common.search') + ' Déesse'}
              className="flex-1 min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 bg-white/5 border border-white/10 rounded-full focus:outline-none focus:border-magenta-500 transition-colors text-sm sm:text-base"
              disabled={loading || isRecording}
              data-testid="film-ia-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim() || isRecording}
              className="p-2.5 sm:p-3 bg-gradient-to-r from-magenta-500 to-violet-600 hover:from-magenta-600 hover:to-violet-700 rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0"
              data-testid="film-ia-send-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Hidden audio element for TTS playback */}
      <audio ref={audioRef} className="hidden" />

      {showMovieGame && <MovieGuessGame onClose={() => setShowMovieGame(false)} />}
      <MediaGeneratorModal
        open={showMediaGen}
        onClose={() => setShowMediaGen(false)}
        onGenerated={(payload) => {
          // Append a synthetic assistant message with the generated media so
          // it appears inline in the conversation like the previous voice→image flow.
          const msg = {
            role: 'assistant',
            content: payload.media === 'image'
              ? `🎨 Image générée : « ${payload.prompt} »`
              : `🎬 Vidéo générée : « ${payload.prompt} »`,
            image_base64: payload.image_b64 || null,
            video_url: payload.video_url || null,
            timestamp: new Date().toISOString(),
          };
          setMessages(prev => [...prev, msg]);
        }}
      />
      {showPosterScanner && (
        <MoviePosterScanner
          onClose={() => setShowPosterScanner(false)}
          onAskMyIA={(text) => {
            setShowPosterScanner(false);
            handleSend(text);
          }}
        />
      )}

      {/* Déesse — Emotional Intelligence modal (v50.11) */}
      {showDeesse && (
        <DeesseVoiceModal onClose={() => setShowDeesse(false)} />
      )}

      {/* Green IA anti-deepfake protection modal (v50.9) */}
      {showGreenIA && greenIA.enabled && (
        <GreenIAPanel onClose={() => setShowGreenIA(false)} />
      )}

      {/* Premium subscription modal (v50.14) */}
      {showSubscription && (
        <SubscriptionModal onClose={() => setShowSubscription(false)} />
      )}

      {/* Studio Pitch modal (v50.10.1) */}
      {showPitchStudio && (
        <div className="fixed inset-0 bg-black z-[9999] overflow-y-auto" data-testid="pitch-studio-modal">
          <div className="min-h-full w-full flex items-start justify-center px-2 pt-[calc(env(safe-area-inset-top)+8px)] pb-28">
            <div className="relative w-full max-w-3xl">
              <button
                onClick={() => setShowPitchStudio(false)}
                className="fixed top-[calc(env(safe-area-inset-top)+10px)] right-3 z-[10000] p-2.5 bg-white/15 hover:bg-white/25 rounded-full"
                data-testid="pitch-studio-close"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <PitchStudio onUpgrade={() => { setShowPitchStudio(false); setShowSubscription(true); }} />
            </div>
          </div>
        </div>
      )}

      {/* Full-screen viewer for AI-generated images */}
      <MediaLightbox
        open={!!lightbox}
        type={lightbox?.type}
        src={lightbox?.src}
        onClose={() => setLightbox(null)}
      />

      <Navigation />
    </div>
  );
};

export default FilmIA;
